import React from 'react';
import { Box, Container, Grid, Typography, Stack, Button, Card, CardContent, Chip, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import SilentMentorFeed from '../components/SilentMentorFeed';
import SchoolIcon from '@mui/icons-material/School';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import TimerIcon from '@mui/icons-material/Timer';
import { Link as RouterLink } from 'react-router-dom';
import Link from '@mui/material/Link';

const MotionBox = motion(Box);

const PERSONA_OPTIONS = [
  { id: '11-12', label: 'Class 11-12 (JEE / NEET / Boards)', desc: 'Physics, Chemistry, Maths, Bio, Commerce & Arts' },
  { id: '9-10', label: 'Class 9-10 (School & Boards)', desc: 'Science, Maths & Social Science Foundation' },
  { id: 'competitive', label: 'Competitive Exams (UPSC / SSC / Banking)', desc: 'Govt Job Prep, GS, Aptitude & Current Affairs' },
  { id: 'degree', label: 'University Degree (B.Com / B.Sc / B.A)', desc: 'Semester Syllabus, Core Subjects & Notes' },
  { id: 'teacher', label: 'Teacher / Educator', desc: 'Manage papers, create tests & track student analytics' },
];

export default function LandingPage() {
  const theme = useTheme();
  const [activePersona, setActivePersona] = React.useState('11-12');

  const UI_TOKENS = {
    focusDeep: '#4B0082',      // Deep Indigo (Ajna / Saturn focus)
    primaryGovt: '#FFD700',    // Golden Yellow (Saraswati Knowledge)
    successLogic: '#50C878',   // Emerald Green (Mercury logic)
    accent: '#4B0082',
    accentLight: '#6F2B9E',
    accentDark: '#300054',
    heroGradient: 'linear-gradient(135deg, #4B0082 0%, #2A0052 100%)',
    heroBg: 'radial-gradient(circle at 80% 0%, rgba(255,215,0,0.18) 0%, rgba(75,0,130,0.04) 45%), #FAFAFE',
    heroChipBg: 'rgba(75,0,130,0.08)',
    heroChipColor: '#4B0082',
    quoteGradient: theme.palette.background.paper,
    footerBg: '#0B0C10',
    cardBorder: '1px solid rgba(75,0,130,0.12)',
    guruZoneBg: 'linear-gradient(135deg, #0B0C10 0%, #1F2833 100%)',
    guruZoneBorder: '#45A29E',
    guruZoneCyan: '#66FCF1',
  };

  const productHighlights = [
    {
      value: '250K+',
      label: 'Practice questions solved every month',
      icon: <QueryStatsIcon fontSize="small" />
    },
    {
      value: '92%',
      label: 'Learners report better retention in 30 days',
      icon: <AutoAwesomeIcon fontSize="small" />
    },
    {
      value: '3x',
      label: 'Faster revision cycles with guided plans',
      icon: <TimerIcon fontSize="small" />
    }
  ];

  const featureCards = [
    {
      title: 'Guru AI Zone ⚡',
      icon: <TimerIcon sx={{ fontSize: 34 }} />,
      text: 'Strict zero-distraction study mode with app-switching guard and emergency call exception rules.',
      color: UI_TOKENS.accent,
      delay: 0.1
    },
    {
      title: 'Hyper-Personalized AI',
      icon: <SchoolIcon sx={{ fontSize: 34 }} />,
      text: 'AI test generator & study hub tailored specifically to your target exam and class level.',
      color: UI_TOKENS.accent,
      delay: 0.2
    },
    {
      title: 'Smart Streak Engine',
      icon: <FitnessCenterIcon sx={{ fontSize: 34 }} />,
      text: 'Track real-time study focus sessions across timezones with zero loss of daily streaks.',
      color: UI_TOKENS.accent,
      delay: 0.3
    },
    {
      title: 'Silent Mentor Nudges',
      icon: <EmojiEventsIcon sx={{ fontSize: 34 }} />,
      text: 'Real-time performance analytics identifying weak concepts and suggesting optimal revision steps.',
      color: UI_TOKENS.accent,
      delay: 0.4
    }
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box
        sx={{
          pt: { xs: 10, md: 15 },
          pb: { xs: 10, md: 14 },
          background: UI_TOKENS.heroBg,
          position: 'relative'
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 4, md: 5 },
              flexDirection: { xs: 'column', md: 'row' }
            }}
          >
            <Box sx={{ flex: { xs: '1 1 100%', md: '0 0 58%' }, width: '100%' }}>
              <MotionBox
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              >
                <Chip
                  label="✨ Hyper-Personalized AI Learning Platform"
                  sx={{
                    mb: 2.5,
                    px: 1,
                    height: 34,
                    fontWeight: 700,
                    bgcolor: UI_TOKENS.heroChipBg,
                    color: UI_TOKENS.heroChipColor,
                    border: '1px solid rgba(255,215,0,0.4)'
                  }}
                />
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '2.4rem', sm: '3rem', md: '3.6rem' },
                    lineHeight: 1.06,
                    mb: 2.5,
                    letterSpacing: '-0.02em'
                  }}
                >
                  Your Personal AI
                  <br />
                  <Box component="span" sx={{
                    background: UI_TOKENS.heroGradient,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    display: 'inline-block'
                  }}>
                    Rank & Study Engine
                  </Box>
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: 'text.secondary',
                    maxWidth: 680,
                    mb: 3,
                    lineHeight: 1.6,
                    fontSize: { xs: '1rem', md: '1.15rem' }
                  }}
                >
                  QuestGuru tailors practice questions, AI mock tests, and study streaks specifically to your academic level—whether you're prepping for NEET/JEE, UPSC, SSC, Boards, or University.
                </Typography>

                {/* Hyper-Personalized Persona Selector */}
                <Box sx={{ mb: 4, p: 2, bgcolor: 'rgba(255,255,255,0.9)', borderRadius: 2, border: UI_TOKENS.cardBorder }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.2, color: 'primary.main' }}>
                    🎯 Select Your Learning Persona:
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {PERSONA_OPTIONS.map((p) => (
                      <Chip
                        key={p.id}
                        label={p.label}
                        clickable
                        color={activePersona === p.id ? 'primary' : 'default'}
                        variant={activePersona === p.id ? 'filled' : 'outlined'}
                        onClick={() => setActivePersona(p.id)}
                        sx={{ fontWeight: activePersona === p.id ? 700 : 500 }}
                      />
                    ))}
                  </Stack>
                </Box>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                  <Button
                    size="large"
                    variant="contained"
                    component={RouterLink}
                    to="/signup"
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      py: 1.6,
                      px: 5,
                      borderRadius: '16px',
                      fontSize: '1rem',
                      fontWeight: 800,
                      backgroundColor: '#4B0082',
                      color: '#FFFFFF',
                      boxShadow: '0 14px 34px rgba(75, 0, 130, 0.28)',
                      '&:hover': {
                        backgroundColor: '#380062',
                        boxShadow: '0 18px 40px rgba(75, 0, 130, 0.38)',
                      }
                    }}
                  >
                    Start Free Personal Journey
                  </Button>
                  <Button
                    size="large"
                    variant="outlined"
                    component={RouterLink}
                    to="/login"
                    sx={{
                      py: 1.6,
                      px: 5,
                      borderRadius: '16px',
                      fontSize: '1rem',
                      fontWeight: 800,
                      color: '#4B0082',
                      borderColor: '#4B0082',
                      borderWidth: 2,
                      '&:hover': { borderWidth: 2, backgroundColor: 'rgba(75,0,130,0.04)' }
                    }}
                  >
                    Log In with Google
                  </Button>
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} sx={{ mt: 3.5 }}>
                  <Chip size="small" label="⚡ Guru AI Zone (Strict Focus)" sx={{ bgcolor: 'rgba(75,0,130,0.08)' }} />
                  <Chip size="small" label="🔥 Dynamic Study Streaks" sx={{ bgcolor: 'rgba(75,0,130,0.08)' }} />
                  <Chip size="small" label="🎯 Persona-Specific AI Mocks" sx={{ bgcolor: 'rgba(75,0,130,0.08)' }} />
                </Stack>
              </MotionBox>
            </Box>

            <Box sx={{ flex: { xs: '1 1 100%', md: '0 0 42%' }, width: '100%' }}>
              <MotionBox
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                sx={{
                  p: { xs: 2.2, sm: 2.8 },
                  borderRadius: 2,
                  border: UI_TOKENS.cardBorder,
                  background: 'linear-gradient(160deg, rgba(255,255,255,0.95) 0%, rgba(248,245,255,0.9) 100%)',
                  boxShadow: '0 24px 60px rgba(75, 0, 130, 0.12)'
                }}
              >
                <Typography sx={{ fontWeight: 800, mb: 1.4 }}>
                  Trusted Learning Outcomes
                </Typography>
                <Typography sx={{ color: 'text.secondary', fontSize: '0.95rem', mb: 2.2 }}>
                  Designed to convert effort into measurable score improvements.
                </Typography>
                <Stack spacing={1.2}>
                  {productHighlights.map((item) => (
                    <Box
                      key={item.label}
                      sx={{
                        p: 1.4,
                        borderRadius: 1.5,
                        border: '1px solid rgba(75,0,130,0.1)',
                        bgcolor: 'rgba(255,255,255,0.8)'
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.7 }}>
                        <Box sx={{ color: UI_TOKENS.accent, display: 'inline-flex' }}>{item.icon}</Box>
                        <Typography sx={{ fontWeight: 800, fontSize: '1.15rem' }}>{item.value}</Typography>
                      </Stack>
                      <Typography sx={{ color: 'text.secondary', fontSize: '0.88rem', lineHeight: 1.4 }}>
                        {item.label}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </MotionBox>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Features Grid */}
      <Container maxWidth="lg" sx={{ mt: { xs: -2, md: -4 }, position: 'relative', zIndex: 2 }}>
        <Stack sx={{ mb: 3 }}>
          <Typography variant="overline" sx={{ letterSpacing: 2, color: 'primary.main', fontWeight: 800 }}>
            Why It Converts Better
          </Typography>
          <Typography variant="h3" sx={{ fontSize: { xs: '1.7rem', md: '2.1rem' }, fontWeight: 800 }}>
            Everything You Need for High-Score Consistency
          </Typography>
        </Stack>
        <Box
          sx={{
            display: 'flex',
            gap: 2.2,
            alignItems: 'stretch',
            flexDirection: { xs: 'column', md: 'row' }
          }}
        >
          {featureCards.map((feature) => (
            <Box key={feature.title} sx={{ flex: 1, minWidth: 0, display: 'flex' }}>
              <MotionBox
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: feature.delay }}
                viewport={{ once: true }}
                sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}
              >
                <Card
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    p: 1,
                    borderRadius: 3,
                    border: UI_TOKENS.cardBorder,
                    background: 'linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(252,250,255,1) 100%)',
                    transition: 'all 0.35s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 20px 40px rgba(75,0,130,0.12)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 2.2, display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <Box sx={{ color: feature.color, mb: 2 }}>{feature.icon}</Box>
                    <Typography
                      variant="h5"
                      sx={{
                        mb: 1.2,
                        fontWeight: 800,
                        fontSize: '1.3rem',
                        lineHeight: 1.25,
                        minHeight: '3.2rem',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      color="text.secondary"
                      sx={{ fontSize: '0.93rem', lineHeight: 1.55, flex: 1 }}
                    >
                      {feature.text}
                    </Typography>
                  </CardContent>
                </Card>
              </MotionBox>
            </Box>
          ))}
        </Box>
      </Container>

      {/* Testimonials removed per request */}

      {/* Silent Mentor Feature */}
      <Box sx={{ py: { xs: 8, md: 11 }, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Grid
            container
            spacing={5}
            alignItems="center"
            sx={{
              borderRadius: 2,
              border: UI_TOKENS.cardBorder,
              background: 'linear-gradient(160deg, rgba(255,215,0,0.06) 0%, rgba(255,255,255,0.9) 52%)',
              p: { xs: 2.5, md: 4 }
            }}
          >
            <Grid item xs={12} md={6}>
              <Typography variant="overline" sx={{ letterSpacing: 2, color: 'primary.main', fontWeight: 800 }}>
                Premium Differentiator
              </Typography>
              <Typography variant="h2" sx={{ mb: 2, fontSize: { xs: '2rem', md: '2.5rem' } }}>
                The Mentor Who <Box component="span" sx={{ color: UI_TOKENS.accent }}>Never Sleeps</Box>
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: '1.08rem', mb: 2.6, lineHeight: 1.65 }}>
                Silent Mentor observes performance patterns in real-time and recommends the exact next action to maximize score lift before your exam.
              </Typography>
              <Stack spacing={1.1} sx={{ mb: 3 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>- Detects weak sub-topics from test behavior</Typography>
                <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>- Suggests next-best study blocks for retention</Typography>
                <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>- Balances speed and accuracy with adaptive nudges</Typography>
              </Stack>
              <Button
                variant="text"
                color="primary"
                size="large"
                sx={{ fontWeight: 800, p: 0 }}
                endIcon={<ArrowForwardIcon />}
              >
                Learn about Silent Mentor
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 30px 60px rgba(75,0,130,0.15)' }}>
                <SilentMentorFeed />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>



      {/* Modern Footer */}
      <Box component="footer" sx={{ py: 8, bgcolor: 'background.paper', color: 'text.primary' }}>
        <Container maxWidth="lg">
          <Grid container spacing={8}>
            <Grid item xs={12} md={4}>
              <Typography variant="h5" sx={{ mb: 1, fontWeight: 900 }}>
                QuestGuru
              </Typography>
              <Typography sx={{ color: 'text.secondary', maxWidth: 360, lineHeight: 1.6 }}>
                A focused preparation platform built to help aspirants turn discipline into rank outcomes.
              </Typography>
            </Grid>

            <Grid item xs={6} md={3}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>Platform</Typography>
              <Stack spacing={2}>
                <Link component={RouterLink} to="/learn" sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { color: UI_TOKENS.accent } }}>Exams Catalog</Link>
                <Link component={RouterLink} to="/study-paths" sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { color: UI_TOKENS.accent } }}>Study Paths</Link>
                <Link component={RouterLink} to="/genai-papers" sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { color: UI_TOKENS.accent } }}>AI Papers</Link>
              </Stack>
            </Grid>

            <Grid item xs={6} md={3}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>Company</Typography>
              <Stack spacing={2}>
                <Link component={RouterLink} to="/contact" sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { color: UI_TOKENS.accent } }}>About Us</Link>
                <Link component={RouterLink} to="/faq" sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { color: UI_TOKENS.accent } }}>Support</Link>
                <Link component={RouterLink} to="/contact" sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { color: UI_TOKENS.accent } }}>Contact</Link>
              </Stack>
            </Grid>

          </Grid>
          <Box sx={{ mt: 7, pt: 3.5, borderTop: '1px solid rgba(0,0,0,0.06)', textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              © {new Date().getFullYear()} Arithmetic Infotech Private Limited.
            </Typography>
            <Stack direction="row" spacing={3} justifyContent="center" sx={{ mt: 2 }}>
              <Link component={RouterLink} to="/privacy" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>Privacy Policy</Link>
              <Link component={RouterLink} to="/terms" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>Terms of Service</Link>
            </Stack>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
